import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  DocumentCategory,
  Document,
  DocumentRequestChecklistItem,
  CheckListDocument
} from 'share/domain/document'
import {
  saveToSessionStorageSelectedDocumentId,
  saveToSessionStorageSelectedDocument,
  saveToSessionStorageSelectedDocumentCategoryId,
  saveToSessionStorageDocumentCategory,
  saveToSessionStorageDocumentOperation,
  saveToSessionStorageDocumentProcessing,
  saveActiveTabToSessionStorage,
  saveDefaultActiveAccordionSessionStorage,
  saveToSessionStorageSelectedDocumentChecklistItemId,
  saveToSessionStorageDocumentChecklist
} from 'reducers/document/documentProcessingSessionStorage'

export interface UploadedDoc {
  id: number
  fileName: string
}

export interface DocumentProcessing {
  activeTab: 'submit' | 'review'
  documentOperation: 'Submit' | 'Review' | null
  selectedDocumentCategoryId: null | number
  selectedDocumentCategory: null | DocumentCategory
  selectedDocumentChecklistItemId: null | number
  selectedDocumentChecklistItem: null | DocumentRequestChecklistItem
  selectedDocumentId: null | number
  selectedDocument: null | Document
  selectedChecklistDocument: null | CheckListDocument
  isCourseCertificateUpload: boolean | null
  selectedDocumentIndex: number | null
  defaultActiveAccordion: number | 0
  documentUploadList: UploadedDoc[]
}

export function getEmptyUploadedDoc(): UploadedDoc {
  const empty: UploadedDoc = {
    id: 0,
    fileName: ''
  }
  return empty
}

export function getEmptyDocumentProcessing(): DocumentProcessing {
  const empty: DocumentProcessing = {
    documentOperation: null,
    selectedDocumentCategoryId: null,
    selectedDocumentCategory: null,
    selectedDocumentChecklistItemId: null,
    selectedDocumentChecklistItem: null,
    selectedDocumentId: null,
    selectedDocument: null,
    selectedChecklistDocument: null,
    isCourseCertificateUpload: false,
    selectedDocumentIndex: null,
    activeTab: 'submit',
    defaultActiveAccordion: 0,
    documentUploadList: []
  }
  return empty
}

const initialState: DocumentProcessing = {
  documentOperation: null,
  selectedDocumentCategoryId: null,
  selectedDocumentCategory: null,
  selectedDocumentChecklistItemId: null,
  selectedDocumentChecklistItem: null,
  selectedDocumentId: null,
  selectedDocument: null,
  selectedChecklistDocument: null,
  isCourseCertificateUpload: false,
  selectedDocumentIndex: null,
  activeTab: 'submit',
  defaultActiveAccordion: 0,
  documentUploadList: []
}

const documentProcessing = createSlice({
  name: 'documentProcessing',
  initialState: initialState,
  reducers: {
    setSelectedDocumentId(state, { payload }: PayloadAction<number | null>) {
      state.selectedDocumentId = payload
      saveToSessionStorageSelectedDocumentId(payload)
      return state
    },
    setSelectedDocument(state, { payload }: PayloadAction<Document | null>) {
      state.selectedDocument = payload
      saveToSessionStorageSelectedDocument(payload)
      return state
    },
    setSelectedDocumentStatus(state, { payload }: PayloadAction<string>) {
      if (state.selectedDocument) {
        state.selectedDocument.status = payload
      }
      return state
    },
    setSelectedDocumentIndex(state, { payload }: PayloadAction<number>) {
      state.selectedDocumentIndex = payload
      return state
    },
    setSelectedDocumentCategoryId(
      state,
      { payload }: PayloadAction<number | null>
    ) {
      state.selectedDocumentCategoryId = payload
      saveToSessionStorageSelectedDocumentCategoryId(payload)
      return state
    },
    setSelectedDocumentCategory(
      state,
      { payload }: PayloadAction<DocumentCategory | null>
    ) {
      state.selectedDocumentCategory = payload
      saveToSessionStorageDocumentCategory(payload)
      return state
    },
    setSelectedDocumentChecklistItemId(
      state,
      { payload }: PayloadAction<number | null>
    ) {
      state.selectedDocumentChecklistItemId = payload
      saveToSessionStorageSelectedDocumentChecklistItemId(payload)

      return state
    },
    setSelectedDocumentChecklistItem(
      state,
      { payload }: PayloadAction<DocumentRequestChecklistItem | null>
    ) {
      state.selectedDocumentChecklistItem = payload
      saveToSessionStorageDocumentChecklist(payload)
      return state
    },
    setSelectedCheckListDocument(
      state,
      { payload }: PayloadAction<CheckListDocument | null>
    ) {
      state.selectedChecklistDocument = payload
      return state
    },

    setDocumentOperation(
      state,
      { payload }: PayloadAction<'Submit' | 'Review' | null>
    ) {
      state.documentOperation = payload
      saveToSessionStorageDocumentOperation(payload)
      return state
    },
    setDocumentProcessing(
      _state,
      { payload }: PayloadAction<DocumentProcessing>
    ) {
      saveToSessionStorageDocumentProcessing(payload)
      return payload
    },
    setIsCourseCertificateUpload(state, { payload }: PayloadAction<boolean>) {
      state.isCourseCertificateUpload = payload
      return state
    },
    setTabSubmit(state) {
      state.activeTab = 'submit'
      saveActiveTabToSessionStorage(state.activeTab)
    },
    setTabReview(state) {
      state.activeTab = 'review'
      saveActiveTabToSessionStorage(state.activeTab)
    },
    setDefaultActiveAccordion(state, { payload }: PayloadAction<number>) {
      state.defaultActiveAccordion = payload
      saveDefaultActiveAccordionSessionStorage(state.defaultActiveAccordion)
    },
    setDocumentUploadList(state, { payload }: PayloadAction<UploadedDoc[]>) {
      state.documentUploadList = payload
      return state
    }
  }
})

export const {
  setDocumentProcessing,
  setSelectedDocumentId,
  setSelectedDocument,
  setSelectedDocumentStatus,
  setSelectedDocumentCategoryId,
  setSelectedDocumentCategory,
  setSelectedDocumentChecklistItemId,
  setSelectedDocumentChecklistItem,
  setSelectedCheckListDocument,
  setDocumentOperation,
  setIsCourseCertificateUpload,
  setSelectedDocumentIndex,
  setTabSubmit,
  setTabReview,
  setDefaultActiveAccordion,
  setDocumentUploadList
} = documentProcessing.actions

export default documentProcessing.reducer
